/**
 * Format utility functions
 */

/**
 * Format file size in bytes to human-readable string
 * @param bytes File size in bytes
 * @returns Formatted string (e.g., "1.5 MB", "234 KB")
 */
export function formatFileSize(bytes: number | null | undefined): string {
    if (bytes == null || bytes === 0) {
        return '0 B';
    }

    const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
    const k = 1024;
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    const size = bytes / Math.pow(k, i);

    // Format with appropriate decimal places
    const formatted = i === 0 ? size.toString() : size.toFixed(2);

    return `${formatted} ${units[i]}`;
}

/**
 * Format a date string or Date object to locale string
 * @param date Date string or Date object
 * @returns Formatted date string
 */
export function formatDate(date: string | Date | null | undefined): string {
    if (!date) {
        return '-';
    }

    const d = typeof date === 'string' ? new Date(date) : date;

    if (isNaN(d.getTime())) {
        return '-';
    }

    return d.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
    });
}

/**
 * Format a date string or Date object to locale date and time string
 * @param date Date string or Date object
 * @returns Formatted date and time string
 */
export function formatDateTime(date: string | Date | null | undefined): string {
    if (!date) {
        return '-';
    }

    const d = typeof date === 'string' ? new Date(date) : date;

    if (isNaN(d.getTime())) {
        return '-';
    }

    return d.toLocaleString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
}

/**
 * Format a number with thousands separator
 * @param num Number to format
 * @returns Formatted number string
 */
export function formatNumber(num: number | null | undefined): string {
    if (num == null) {
        return '0';
    }

    return num.toLocaleString();
}
